import InputDate from "core/inputs/input-date/InputDate";
import InputPicture from "core/inputs/input-picture/InputPicture";
import InputSearchList from "core/inputs/input-search/InputSearchList";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import InputSelect from "core/inputs/input-select/InputSelect";
import InputText from "core/inputs/input-text/InputText";
import InputTextArea from "core/inputs/input-text-area/InputTextArea";
import InputTextSearch from "core/inputs/input-text-search/InputTextSearch";
import ToggleSwitchButtonMission from "core/buttons/toggle-switch-button-mission/ToggleSwitchButtonMission";
import _ from "lodash";
import { Controller } from "utils/Controller";
import {
  TAG_TYPES,
  LANG,
  RECRUITMENT_METHOD,
  VAR_TYPES,
  MAX_LENGTH,
  CONTRACT_TYPES,
  FILTERS_FOR_NAMES,
  MINCHARMISSIONNAME,
  MAXCHARMISSIONNAME,
  FRANCE_NATIONALITY_ID,
} from "constants/constants";
import { isValidSirenLuhn } from "utils/functions";
import {
  FORM_INFO,
  GENDER_OPTIONS,
  REMOTE_DAYS_NUMBER,
  RULES,
  MISSION_FORM_LABELS,
  BILLING_TYPE,
  CONSULTANT_FORM_LABELS,
  STAFF_FORM_LABELS,
  STAFF_FORM_PLACEHOLDERS,
  CLIENT_FORM_LABELS,
  CLIENT_FORM_PLACEHOLDERS,
  JOB_CATEGORY_NONE,
  JOB_LOCKED_PLACEHOLDER,
} from "constants/forms";
import { MISSION_MANAGER_ROLES } from "access-roles/roles";
import {
  formattedCountry,
  getJobCategoryOptions,
  stringLengthWithoutSpaces,
} from "utils/helper";
import { getFilteredList } from "utils/consultantHelper";

export const formatNames = (list) =>
  list?.map((elm) => ({
    value: elm?.id,
    label: elm?.name,
  }));
export const getNameById = ({ list, id }) =>
  list
    ?.filter((elm) => elm.id === Number(id))
    ?.map((elm) => ({
      value: elm.id,
      label: elm.full_name || elm.name,
    }));
export const getTranslatedList = (list, langage, type) =>
  (list || []).map((elm) => ({
    label: langage === LANG.EN ? elm?.en : elm?.fr,
    value: elm?.id,
    type: type || TAG_TYPES.FUNCTIONS,
  }));
export const getTranslatedProfilesList = (list, langage) =>
  (list || []).map((elm) => ({
    label: langage === LANG.EN ? elm?.en : elm?.fr,
    value: elm?.id,
    type: TAG_TYPES.PROFILES,
  }));

export const formatUsersOptions = ({ users = [], type }) => {
  return users?.map((elm) => ({
    profile: elm?.profile_id,
    value: elm.id,
    type: type || TAG_TYPES.CSLT_STR,
    label: `${elm?.first_name} ${elm?.last_name}` || elm?.full_name,
  }));
};

export const formatUsersWithProfile = (users, profiles, type) =>
  _.chain(users)
    .filter((elm) => _.includes(profiles, elm?.profile_id))
    .map((elm) => ({
      profile: elm?.profile_id,
      value: elm.id,
      type,
      label: `${elm?.first_name} ${elm?.last_name}`,
    }))
    .value();

export const formatNamesByFilters = (users, type) =>
  _.chain(users)
    .map((elm) => ({
      profile: elm?.profile_id || "",
      value: elm.id,
      type,
      label: elm?.full_name,
    }))
    .value();

export const formatClientOptions = (client = []) => {
  const typeFormatter = (type) => {
    if (type === FILTERS_FOR_NAMES.CLIENT) return TAG_TYPES.CLIENTS;
    if (type === FILTERS_FOR_NAMES.PROSPECTION) return TAG_TYPES.PROSPECTIONS;
  };
  return client.map((elm) => ({
    value: elm.id,
    // `type` comes from the name search, `user_type` from the general search.
    type: typeFormatter(elm?.type ?? elm?.user_type) || TAG_TYPES.CLIENTS,
    label: elm?.name || elm.full_name,
  }));
};

export const formatNamesOptions = ({ type, list = [] }) =>
  list.map((elm) => ({
    value: elm.id,
    type: type || TAG_TYPES.PEOPLE,
    label: `${elm?.name}`,
  }));

export const formatAllUsers = (users, type) =>
  _.chain(users)
    .map((elm) => ({
      value: elm.id,
      type,
      label: `${elm.first_name} ${elm.last_name}`,
    }))
    .value();

export const formatUsersOptionsTag = (users = []) =>
  users.map((elm) => ({
    profile: elm?.profile_id,
    value: elm.id,
    label: `${elm?.first_name} ${elm?.last_name}`,
  }));

export const onDeleteUpdateOptions = (selectedOptions, val) =>
  selectedOptions?.filter((elem) => elem.value !== val);

export const renderInputReasonLeave = ({
  control,
  errors,
  readOnly,
  register,
  setValue,
  watch,
  leavingReasons,
  leaving_reason_id,
  value,
  lang,
}) => {
  const handleDelete = () => setValue("leaving_reason_id", []);
  const handleSetValue = (val) => setValue("leaving_reason_id", val);
  return (
    <Controller
      {...{
        control,
        register,
        name: "leaving_reason_id",
        rules: {
          required:
            watch("end_date") || watch("contract_type") === CONTRACT_TYPES.CDD,
        },
        render: (props) => (
          <InputSearchTag
            id="leaving_reason_id"
            label={STAFF_FORM_LABELS.leaving_reason_id}
            limitChoice={FORM_INFO.leaving_reason.limitChoice}
            onDelete={handleDelete}
            options={getTranslatedList(
              leavingReasons,
              lang,
              TAG_TYPES.LEAVING_REASON
            )}
            placeholder={STAFF_FORM_PLACEHOLDERS.leaving_reason_id}
            popup={FORM_INFO.leaving_reason.popup}
            readOnly={readOnly}
            selectedOptions={leaving_reason_id || value}
            required={
              watch("end_date") || watch("contract_type") === CONTRACT_TYPES.CDD
            }
            setValue={handleSetValue}
            errors={errors["leaving_reason_id"]}
            type={TAG_TYPES.LEAVING_REASON}
            {...props}
          />
        ),
      }}
    />
  );
};

export const renderInputLinkedin = ({
  control,
  errors,
  label,
  placeholder,
  readOnly,
  register,
  required,
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "client_contact_linkedin",
      rules: {
        required: required,
        pattern: {
          value: RULES.linkedin_url,
          message: "linkedin_url",
        },
      },
      render: (props) => (
        <InputText
          id={"client_contact_linkedin"}
          required={required}
          label={label}
          placeholder={placeholder}
          readOnly={readOnly}
          errors={errors["client_contact_linkedin"]}
          errorMessage={errorMessage}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputClientContact = ({
  control,
  errors,
  label,
  placeholder,
  readOnly,
  register,
  name,
  required,
  pattern,
  patternMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: name,
      rules: {
        required: required,
        ...(pattern && {
          pattern: {
            value: pattern,
            message: patternMessage || "invalid_format",
          },
        }),
      },
      render: (props) => (
        <InputText
          id={name}
          label={label}
          placeholder={placeholder}
          required={required}
          readOnly={readOnly}
          errors={errors[name]}
          errorMessage={errors[name]?.message || null}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputFeeComment = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
  name,
  width,
  required,
  pattern,
  patternMessage,
  value,
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: name,
      rules: {
        required: required,
        pattern: {
          value: pattern,
          message: patternMessage,
        },
      },
      render: (props) => (
        <InputTextArea
          id={name}
          required={required}
          label={label}
          placeholder={placeholder}
          readOnly={readOnly}
          width={width}
          value={value}
          errors={errors[name]}
          errorMessage={errorMessage}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputPurchasingContact = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "purchasing_contact",
      render: (props) => (
        <InputText
          id={"purchasing_contact"}
          label={label}
          placeholder={placeholder}
          readOnly={readOnly}
          errors={errors["purchasing_contact"]}
          {...props}
        />
      ),
    }}
  />
);

export const renderTADSelect = ({
  control,
  errors,
  options,
  label,
  placeholder,
  readOnly,
  register,
  setValue,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "remote_work_id",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSelect
          id="remote_work_id"
          label={label}
          placeholder={placeholder}
          options={options}
          required
          setValue={setValue}
          readOnly={readOnly}
          errors={errors["remote_work_id"]}
          {...props}
        />
      ),
    }}
  />
);

export const renderMissionFeeInput = ({
  control,
  errors,
  label,
  placeholder,
  readOnly,
  register,
  setValue,
  value,
  required,
  name,
  pattern,
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: name,
      rules: {
        required: required,
        pattern: pattern || null,
      },
      render: (props) => {
        return (
          <InputText
            {...props}
            errorMessage={errorMessage}
            id={name}
            label={label}
            placeholder={placeholder}
            required={required}
            value={value}
            setValue={setValue}
            readOnly={readOnly}
            errors={errors[name]}
          />
        );
      },
    }}
  />
);

export const renderAppealSelect = ({
  control,
  errors,
  options,
  label,
  placeholder,
  readOnly,
  register,
  setValue,
  clearErrors,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "appeal_id",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSelect
          id="appeal_id"
          label={label}
          placeholder={placeholder}
          options={options}
          required
          setValue={(name, val) => {
            setValue(name, val);
            if (val) {
              clearErrors?.(name);
            }
          }}
          readOnly={readOnly}
          errors={errors["appeal_id"]}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputCurrency = ({
  label,
  placeholder,
  control,
  register,
  currencyList,
  setValue,
  readOnly,
  errors,
  value,
  errorMessage,
  errorValue,
  required = true,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "currency_id",
      rules: {
        required,
      },
      render: (props) => (
        <InputSelect
          errorValue={errorValue}
          errorMessage={errorMessage}
          id="currency_id"
          label={label}
          placeholder={placeholder}
          options={currencyList?.map((elm) => ({
            label: elm.currency_symbol,
            value: elm.id,
            id: elm.id,
          }))}
          required={required}
          setValue={setValue}
          value={value}
          readOnly={readOnly}
          errors={errors["currency_id"]}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputHonorary = ({
  control,
  errors = {},
  label,
  placeholder,
  readOnly,
  register,
  setValue,
  name,
  value,
  onChange,
  errorValue,
  errorMessage,
  required = true,
}) => {
  return (
    <Controller
      {...{
        control,
        register,
        name: name,
        rules: {
          required: required,
          min: { value: 0, message: "positive_value_required" },
        },
        render: (props) => (
          <InputText
            errorValue={errorValue}
            errorMessage={errorMessage}
            id={name}
            type={VAR_TYPES.NUMBER}
            min={0}
            errors={errors[name]}
            label={label}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            readOnly={readOnly}
            required={required}
            setValue={setValue}
            {...props}
          />
        ),
      }}
    />
  );
};

export const renderInputADV = ({
  control,
  getOptions,
  label,
  options,
  placeholder,
  readOnly,
  register,
  required,
  setValue,
  value,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "adv_id",
      rules: {
        required,
      },
      render: (props) => (
        <InputSearchTag
          errors={errors["adv_id"]}
          getOptions={getOptions}
          label={label}
          limitChoice={FORM_INFO.ADV.limitChoice}
          minLength={2}
          onDelete={() => setValue("adv_id", [])}
          options={options}
          placeholder={placeholder}
          popup={FORM_INFO.ADV.popup}
          readOnly={readOnly}
          required={required}
          selectedOptions={value}
          setValue={(val) => setValue("adv_id", val)}
          type={TAG_TYPES.PEOPLE}
          id="adv"
          {...props}
        />
      ),
    }}
  />
);

export const renderInputCsltStr = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  setValue,
  value,
  getNamesByFilters,
  namesByFilters,
  errors = {},
  profile = ["cslt", "str"],
  clearErrors,
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "worker_id",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSearchTag
          getOptions={(search) =>
            getNamesByFilters({
              types: [FILTERS_FOR_NAMES.PERSON],
              profile,
              search,
            })
          }
          label={label}
          limitChoice={FORM_INFO.CSLT_STR.limitChoice}
          minLength={2}
          onDelete={() => setValue("worker_id", [])}
          options={formatUsersWithProfile(
            namesByFilters,
            profile,
            TAG_TYPES.CSLT_STR
          )}
          placeholder={placeholder}
          popup={FORM_INFO.CSLT_STR.popup}
          readOnly={readOnly}
          required
          selectedOptions={value}
          setValue={(val) => {
            setValue("worker_id", val);
            if (Array.isArray(val) && val.length > 0) {
              clearErrors?.("worker_id");
            }
          }}
          type={TAG_TYPES.CSLT_STR}
          errors={errors["worker_id"]}
          errorMessage={errorMessage}
          id="worker"
          {...props}
        />
      ),
    }}
  />
);

export const renderInputMissionDescription = ({
  control,
  label,
  placeholder,
  readOnly,
  value,
  register,
  errors = {},
  isMission,
  errorMessage,
  errorValue,
  additionalErrorMessage,
  additionalErrorValue,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "description",
      rules: {
        required: true,
        validate: {
          longueurMinimum: (value) => value.split(/\s+/).length >= 50,
          minimumTirets: (value) =>
            (value.match(/[-•◦▪▫▸➔★✔✗♦◊❏❖➢]/g) || []).length >= 5,
        },
      },
      render: (props) => (
        <InputTextArea
          additionalErrorValue={additionalErrorValue}
          additionalErrorMessage={additionalErrorMessage}
          errorMessage={errorMessage}
          errorValue={errorValue}
          readOnly={readOnly}
          value={value}
          label={label}
          id={"mission_description"}
          placeholder={placeholder}
          required
          isMission={isMission}
          errors={errors["description"]}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputMissionDates = ({
  clearErrors,
  control,
  label,
  lang,
  readOnly,
  register,
  errors = {},
  name,
  required,
  id,
  min,
  max,
  errorMessage,
  errorValue,
  additionalErrorMessage,
  additionalErrorValue,
  dateOrderErrorMessage,
  setValue,
  value,
}) => {
  return (
    <Controller
      {...{
        control,
        register,
        name: name,
        rules: {
          required: required,
          validate: {
            dateOrder: (value) => {
              if (!value || !min) return true;
              return (
                new Date(value) >= new Date(min) ||
                dateOrderErrorMessage ||
                "date_order_error"
              );
            },
          },
        },
        render: () => (
          <InputDate
            clearErrors={clearErrors}
            additionalErrorMessage={additionalErrorMessage}
            additionalErrorValue={additionalErrorValue}
            errorMessage={errorMessage}
            errorValue={errorValue}
            id={id}
            readOnly={readOnly}
            label={label}
            required={required}
            errors={errors[name]}
            lang={lang}
            min={min}
            max={max}
            onChange={(e) => {
              setValue(name, e.target.value);
              if (e.target.value) {
                clearErrors?.(name);
              }
            }}
            value={value}
            name={name}
          />
        ),
      }}
    />
  );
};

export const renderInputMissionBillingCondition = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
  id,
  width,
  isMission,
  name,
}) => (
  <Controller
    {...{
      control,
      register,
      name: name,
      render: (props) => (
        <InputTextArea
          readOnly={readOnly}
          label={label}
          id={id}
          placeholder={placeholder}
          isMission={isMission}
          errors={errors[name]}
          width={width}
          name={name}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputMissionType = ({
  control,
  register,
  errors,
  setValue,
  readOnly,
  label,
  placeholder,
  options,
  errorValue,
  errorMessage,
  clearErrors,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "type_",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSelect
          id={"type_"}
          label={label}
          placeholder={placeholder}
          readOnly={readOnly}
          errors={errors["type_"]}
          setValue={(name, val) => {
            setValue(name, val);
            if (val) {
              clearErrors?.(name);
            }
          }}
          required
          options={options}
          errorValue={errorValue}
          errorMessage={errorMessage}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputMissionName = ({
  control,
  register,
  errors,
  setValue,
  readOnly,
  label,
  placeholder,
  errorMessage,
  errorValue,
  value,
  setMissingCharValue,
  setErrorNameMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "name",
      rules: {
        required: true,
        validate: {
          missionNameLength: (value) => {
            const missionNameLengthWithoutSpaces = stringLengthWithoutSpaces({
              string: value,
            });
            const diffMin = MINCHARMISSIONNAME - missionNameLengthWithoutSpaces;
            const diffMax = missionNameLengthWithoutSpaces - MAXCHARMISSIONNAME;

            if (diffMin > 0) {
              setMissingCharValue(diffMin);
              setErrorNameMessage("mission_name_error_message_missing");
              return false;
            }

            if (diffMax > 0) {
              setMissingCharValue(diffMax);
              setErrorNameMessage("mission_name_error_message_too_long");
              return false;
            }

            setMissingCharValue(0);
            setErrorNameMessage("");
            return true;
          },
        },
      },
      id: "name",
      render: (props) => (
        <InputText
          label={label}
          placeholder={placeholder}
          readOnly={readOnly}
          errors={errors["name"]}
          setValue={setValue}
          value={value}
          required
          errorMessage={errorMessage}
          errorValue={errorValue}
          id={"mission_name"}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputComment = ({
  comment,
  commentName,
  commentSettings,
  control,
  label,
  placeholder,
  readOnly,
  register,
  setValue,
  errors = {},
  name,
  required,
  id,
}) => (
  <Controller
    {...{
      comment,
      control,
      register,
      name: name,
      rules: {
        pattern: {
          value: RULES.positive_max_99,
        },
        required: required,
      },
      render: (props) => (
        <InputText
          errors={errors[name]}
          label={label}
          placeholder={placeholder}
          readOnly={readOnly}
          required={required}
          comment={comment}
          commentSettings={commentSettings}
          setComment={(val) => setValue(commentName, val)}
          type={VAR_TYPES.NUMBER}
          id={id}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputStrCompanyName = ({
  control,
  register,
  readOnly,
  label,
  placeholder,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "company_name",
      id: "company_name",
      render: (props) => (
        <InputText
          label={label}
          placeholder={placeholder}
          readOnly={readOnly}
          id="company_name"
          {...props}
        />
      ),
    }}
  />
);

export const renderInputSiren = ({
  control,
  register,
  errors,
  setValue,
  readOnly,
  name,
  label,
  placeholder,
  rules,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "siren",
      rules,
      id: "siren",
      render: (props) => (
        <InputText
          label={label}
          placeholder={placeholder}
          readOnly={readOnly}
          errors={errors[name]}
          setValue={setValue}
          id="siren"
          {...props}
        />
      ),
    }}
  />
);

export const renderInputSiret = ({
  control,
  setValue,
  readOnly,
  label,
  placeholder,
  errors = {},
  register,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "siret",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputText
          required
          label={label}
          placeholder={placeholder}
          setValue={setValue}
          readOnly={readOnly}
          errors={errors["siret"]}
          id="siret"
          {...props}
        />
      ),
    }}
  />
);

export const renderInputStartContract = ({
  control,
  label,
  readOnly,
  register,
  lang,
  errors = {},
  setValue,
  value,
}) => {
  register("start_date", { required: true });
  return (
    <InputDate
      id={FORM_INFO.start_date.id}
      label={label}
      readOnly={readOnly}
      required
      errors={errors["start_date"]}
      lang={lang}
      onChange={(e) => setValue("start_date", e.target.value)}
      value={value}
    />
  );
};

export const renderInputEndContract = ({
  label,
  readOnly,
  register,
  lang,
  contract_type,
  errors = {},
  setValue,
  value,
}) => {
  const unRequiredContractTypes = [
    CONTRACT_TYPES.CDI,
    CONTRACT_TYPES.REPRESENTATIVE,
  ];
  const isRequired = !unRequiredContractTypes.includes(contract_type);
  register("end_date", {
    required: isRequired,
  });

  return (
    <InputDate
      id={FORM_INFO.end_date.id}
      label={label}
      readOnly={readOnly}
      required={isRequired}
      errors={errors["end_date"]}
      lang={lang}
      onChange={(e) => setValue("end_date", e.target.value)}
      value={value}
    />
  );
};

export const renderInputDate = ({
  control,
  label,
  readOnly,
  register,
  lang,
  errors = {},
  name,
  id,
  required,
}) => (
  <Controller
    {...{
      control,
      register,
      name,
      rules: {
        required,
      },
      render: (props) => (
        <InputDate
          id={id}
          label={label}
          readOnly={readOnly}
          required={required}
          errors={errors[name]}
          lang={lang}
          {...props}
        />
      ),
    }}
  />
);
export const renderInputArrivalDate = ({
  control,
  label,
  readOnly,
  register,
  lang,
  errors = {},
  setValue,
  value,
}) => {
  register("arrival_date", { required: true });
  return (
    <InputDate
      id={FORM_INFO.arrival_date.id}
      label={label}
      lang={lang}
      readOnly={readOnly}
      required
      errors={errors["arrival_date"]}
      onChange={(e) => setValue("arrival_date", e.target.value)}
      value={value}
    />
  );
};

export const renderInputStartDateContract = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
  required,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "contract_start_date",
      rules: {
        required: required || true,
      },
      render: (props) => (
        <InputText
          label={label}
          placeholder={placeholder}
          readOnly={readOnly}
          required
          errors={errors["contract_start_date"]}
          id="contract_start_date"
          {...props}
        />
      ),
    }}
  />
);

export const renderInputBranch = ({
  branches,
  control,
  label,
  placeholder,
  readOnly,
  register,
  setValue,
  value,
  errors = {},
  name,
  type,
  filter,
  selectedOptions,
  required = true,
  limitChoice,
  errorValue,
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: filter || name || "branch_id",
      rules: {
        required: required,
      },
      render: (props) => (
        <InputSearchTag
          errorMessage={errorMessage}
          errorValue={errorValue}
          label={label}
          limitChoice={limitChoice || FORM_INFO.branch.limitChoice}
          onDelete={
            selectedOptions
              ? (val) =>
                  setValue(filter, onDeleteUpdateOptions(selectedOptions, val))
              : () => setValue(name || "branch_id", [])
          }
          options={formatNames(branches)}
          placeholder={placeholder}
          popup={FORM_INFO.branch.popup}
          readOnly={readOnly}
          required={required}
          selectedOptions={selectedOptions || value}
          setValue={(val) => setValue(filter || name || "branch_id", val)}
          type={type || TAG_TYPES.BRANCHES}
          errors={errors || errors[filter || name || "branch_id"]}
          id={filter || "branch"}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputAssignedBranches = ({
  control,
  register,
  setValue,
  assigned_branches,
  branches,
  readOnly,
  apiErrorResponse,
  errors,
  isAssignedBranchesRequired,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "assigned_branches",
      rules: {
        required: isAssignedBranchesRequired,
      },
      render: (props) => (
        <InputSearchTag
          label={STAFF_FORM_LABELS.assigned_branches}
          limitChoice={10}
          onDelete={(val) =>
            setValue(
              "assigned_branches",
              getFilteredList({ list: assigned_branches, value: val })
            )
          }
          options={formatNames(branches)}
          placeholder={STAFF_FORM_PLACEHOLDERS.assigned_branches}
          popup={FORM_INFO.branch.popup}
          readOnly={readOnly}
          required={isAssignedBranchesRequired}
          selectedOptions={assigned_branches}
          setValue={(val) => setValue("assigned_branches", val)}
          type={TAG_TYPES.BRANCHES}
          errors={errors["assigned_branches"]}
          errorMessage={
            apiErrorResponse?.assigned_branches && "api_error_assigned_branches"
          }
          id="assigned-branches"
          {...props}
        />
      ),
    }}
  />
);

export const renderContractType = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  setValue,
  options,
  errors = {},
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "contract_type",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSelect
          id="contract_type"
          maxHeight={160}
          label={label}
          placeholder={placeholder}
          options={options}
          required
          setValue={setValue}
          readOnly={readOnly}
          errors={errors}
          errorMessage={errorMessage}
          {...props}
        />
      ),
    }}
  />
);

// Precedes the job field everywhere a job is entered: picking a category
// restricts the job options. Scalar value, unlike the job field which holds a
// tag array.
//
// DERIVED, never submitted (redmine 12940): a job belongs to exactly one
// category, so picking the job settles it. The field stays on screen because it
// narrows a job list that went from 34 to 80 entries, but it is not required —
// requiring a field the user never has to fill would block the form for nothing.
export const renderInputJobCategory = ({
  control,
  jobCategories,
  label,
  lang,
  readOnly,
  register,
  setValue,
  errors = {},
  errorMessage,
  clearErrors,
  required = false,
  name = "domain_category_id",
}) => (
  <Controller
    {...{
      control,
      register,
      name,
      rules: {
        // A read-only field can never be filled, so it must never block the
        // form. Derived fields are not required either (see the note above).
        required: required && !readOnly,
      },
      render: (props) => (
        <InputSelect
          id={name}
          maxHeight={160}
          label={label}
          options={getJobCategoryOptions({ lang, list: jobCategories })}
          emptyLabel={
            jobCategories?.find((category) => category.id === props.value)
              ?.is_protected
              ? JOB_CATEGORY_NONE
              : undefined
          }
          readOnly={readOnly}
          required={required}
          errors={errors[name]}
          errorMessage={errorMessage}
          setValue={(fieldName, val) => {
            setValue(fieldName, val);
            if (val) {
              clearErrors?.(fieldName);
            }
          }}
          {...props}
        />
      ),
    }}
  />
);

// Multi-valued variant of the category field, for the filter screens whose job
// filter is multi-valued too. renderInputJobCategory is the scalar entry form.
export const renderInputJobCategoryTag = ({
  control,
  jobCategories,
  label,
  lang,
  register,
  setValue,
  selectedOptions,
  filter,
  limitChoice,
  clearAllLabel,
  // Opt-in: the category is an optional filter on the shared dashboard, but the
  // salary abacus needs a criterion to search on and the job can no longer be
  // that one — it is locked until a category is picked (redmine 12939).
  required = false,
  errors = {},
  clearErrors,
}) => (
  <Controller
    {...{
      control,
      register,
      name: filter,
      rules: { required },
      render: () => (
        <InputSearchTag
          label={label}
          limitChoice={
            limitChoice || FORM_INFO.dav_function_category.limitChoice
          }
          onDelete={(val) =>
            setValue(filter, onDeleteUpdateOptions(selectedOptions, val))
          }
          options={getJobCategoryOptions({
            lang,
            list: jobCategories,
            type: TAG_TYPES.FUNCTIONS,
          })}
          popup={FORM_INFO.dav_function_category.popup}
          required={required}
          errors={errors[filter]}
          selectedOptions={selectedOptions}
          setValue={(val) => {
            setValue(filter, val);
            // Without this the required error stays on screen until the next
            // submit: the custom Controller wires no onChange, so RHF's
            // reValidateMode never fires on its own.
            if (val?.length) clearErrors?.(filter);
          }}
          type={TAG_TYPES.FUNCTIONS}
          id={filter}
          clearAllLabel={clearAllLabel}
        />
      ),
    }}
  />
);

// `lockedUntilCategory`: the job cannot be picked before a category is (redmine
// 12939). Kept apart from `readOnly` so the field stays visible with the
// placeholder that says why, instead of collapsing to the consultation "-".
export const renderInputCsltJob = ({
  control,
  davFunctions,
  label,
  lang,
  placeholder,
  readOnly,
  register,
  setValue,
  value,
  errors = {},
  errorValue,
  errorMessage,
  clearErrors,
  lockedUntilCategory = false,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "domain_id",
      rules: {
        validate: (value) => Array.isArray(value) && value.length > 0,
      },
      render: (props) => (
        <InputSearchTag
          label={label}
          limitChoice={1}
          onDelete={() => setValue("domain_id", [])}
          options={getTranslatedList(davFunctions, lang)}
          placeholder={
            lockedUntilCategory ? JOB_LOCKED_PLACEHOLDER : placeholder
          }
          popup={FORM_INFO.dav_function.popup}
          readOnly={readOnly}
          disabled={lockedUntilCategory}
          required
          errorValue={errorValue}
          errorMessage={errorMessage}
          selectedOptions={value}
          setValue={(val) => {
            setValue("domain_id", val);
            if (Array.isArray(val) && val.length > 0) {
              clearErrors?.("domain_id");
            }
          }}
          type={TAG_TYPES.FUNCTIONS}
          errors={errors["domain_id"]}
          id="domain_id"
          {...props}
        />
      ),
    }}
  />
);

export const renderInputJob = ({
  control,
  davFunctions,
  label,
  lang,
  placeholder,
  readOnly,
  register,
  setValue,
  value,
  errors = {},
  filter,
  selectedOptions,
  required = true,
  limitChoice,
  errorMessage,
  lockedUntilCategory = false,
}) => (
  <Controller
    {...{
      control,
      register,
      name: filter || "domain_id",
      rules: {
        required: required,
      },
      render: (props) => (
        <InputSearchTag
          errorMessage={errorMessage}
          label={label}
          limitChoice={limitChoice || FORM_INFO.dav_function.limitChoice}
          onDelete={
            selectedOptions
              ? (val) =>
                  setValue(filter, onDeleteUpdateOptions(selectedOptions, val))
              : () => setValue("domain_id", [])
          }
          options={getTranslatedList(davFunctions, lang)}
          placeholder={
            lockedUntilCategory ? JOB_LOCKED_PLACEHOLDER : placeholder
          }
          popup={FORM_INFO.dav_function.popup}
          readOnly={readOnly}
          disabled={lockedUntilCategory}
          required={required}
          selectedOptions={selectedOptions || value}
          setValue={(val) => setValue(filter || "domain_id", val)}
          type={TAG_TYPES.FUNCTIONS}
          // Already scoped by the caller: ConsultantCorporateInfoForm hands in
          // `errors["domain_id"] || apiErrorResponse?.["domain_id"]`.
          // Dereferencing it a second time here loses the error entirely.
          errors={errors}
          id={filter || "domain_id"}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputProfile = ({
  profileList,
  control,
  label,
  lang,
  placeholder,
  readOnly,
  register,
  setValue,
  value,
  errors = {},
  type,
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "profile_id",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSearchTag
          errorMessage={errorMessage}
          label={label}
          limitChoice={FORM_INFO.profile.limitChoice}
          onDelete={() => setValue("profile_id", [])}
          options={getTranslatedProfilesList(profileList, lang)}
          placeholder={placeholder}
          popup={FORM_INFO.profile.popup}
          readOnly={readOnly}
          required
          selectedOptions={value}
          setValue={(val) => setValue("profile_id", val)}
          type={type || TAG_TYPES.PROFILES}
          errors={errors}
          id="profile_id"
          {...props}
        />
      ),
    }}
  />
);

export const renderInputRemoteDays = ({
  control,
  readOnly,
  register,
  setValue,
  errors = {},
  errorMessage,
  errorValue,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "non_remote_days_number",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSelect
          id="non_remote_days_number"
          label={MISSION_FORM_LABELS.non_remote_days_number}
          options={REMOTE_DAYS_NUMBER}
          placeholder={MISSION_FORM_LABELS.non_remote_days_number}
          readOnly={readOnly}
          required
          setValue={setValue}
          errors={errors["non_remote_days_number"]}
          errorMessage={errorMessage}
          errorValue={errorValue}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputSwitch = ({
  control,
  readOnly,
  register,
  setValue,
  value,
  errors = {},
  name,
  key,
  data,
  clearErrors,
}) => (
  <Controller
    {...{
      control,
      register,
      name,
      defaultValue: value ?? null,
      rules: {
        validate: (value) => typeof value === "boolean",
      },
      render: (props) => (
        <ToggleSwitchButtonMission
          key={key}
          data={data}
          value={value}
          onChange={(val) => {
            setValue(name, val);
            if (props.field) {
              props.field.onChange(val);
            }
            if (typeof val === "boolean") {
              clearErrors?.(name);
            }
          }}
          errors={errors[name]}
          readOnly={readOnly}
          required
          topUp={data}
          id={name}
        />
      ),
    }}
  />
);

export const renderInputManager = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  setValue,
  value,
  errors = {},
  errorValue,
  errorMessage,
  filter,
  selectedOptions,
  limitChoice,
  required = true,
  name,
  getNamesByFilters,
  namesByFilters,
  role = MISSION_MANAGER_ROLES,
}) => (
  <Controller
    {...{
      control,
      register,
      name: name || filter || "manager_id",
      rules: {
        required: required,
      },
      render: (props) => (
        <InputSearchTag
          errors={errors}
          getOptions={(search) =>
            getNamesByFilters({
              role,
              types: [FILTERS_FOR_NAMES.PERSON],
              search,
            })
          }
          label={label}
          limitChoice={limitChoice || FORM_INFO.manager.limitChoice}
          minLength={2}
          onDelete={(val) => {
            setValue(
              name || filter || "manager_id",
              onDeleteUpdateOptions(selectedOptions, val)
            );
          }}
          options={formatUsersOptions({
            users: namesByFilters,
            type: TAG_TYPES.PEOPLE,
          })}
          placeholder={placeholder}
          popup={FORM_INFO.manager.popup}
          readOnly={readOnly}
          required={required}
          selectedOptions={value || selectedOptions}
          setValue={(val) => setValue(name || filter || "manager_id", val)}
          type={TAG_TYPES.PEOPLE}
          id={name || filter || "manager_id"}
          errorValue={errorValue}
          errorMessage={errorMessage}
          {...props}
        />
      ),
    }}
  />
);

export const displaySalary = ({ control, register, readOnly }) => {
  return (
    <Controller
      {...{
        control,
        register,
        name: "salary.value",
        render: (props) => (
          <InputText
            required={true}
            readOnly={readOnly}
            label={CONSULTANT_FORM_LABELS.salary}
            {...props}
          />
        ),
      }}
    />
  );
};

export const renderPersonalManagement = ({
  control,
  getOptions,
  label,
  placeholder,
  readOnly,
  register,
  setValue,
  value,
  errors,
  options,
  errorMessage,
}) => {
  return (
    <Controller
      {...{
        control,
        register,
        name: "gp_id",
        rules: {
          required: true,
        },
        render: (props) => (
          <InputSearchTag
            errorMessage={errorMessage}
            errors={errors}
            getOptions={getOptions}
            label={label}
            limitChoice={FORM_INFO.personal_management.limitChoice}
            minLength={2}
            onDelete={() => setValue("gp_id", [])}
            options={options}
            placeholder={placeholder}
            popup={FORM_INFO.manager.popup}
            readOnly={readOnly}
            required
            selectedOptions={value}
            setValue={(val) => setValue("gp_id", val)}
            type={TAG_TYPES.PEOPLE}
            id="gp_id"
            {...props}
          />
        ),
      }}
    />
  );
};

export const renderInputSector = ({
  control,
  register,
  setValue,
  value,
  errors,
  readOnly,
  sectorList,
  lang,
}) => {
  const options = sectorList?.map((elm) => ({
    label: lang === LANG.FR ? elm.fr : elm.en,
    value: elm.id,
  }));

  const handleSetValue = (name, selectedValue) => {
    const selectedOption = options?.find(
      (opt) => String(opt.value) === String(selectedValue)
    );
    setValue(name, selectedOption ? [selectedOption] : []);
  };

  return (
    <Controller
      {...{
        control,
        register,
        name: "sector_id",
        rules: {
          required: true,
        },
        render: (props) => {
          const extractValue = (val) => {
            if (Array.isArray(val)) return val[0]?.value ?? "";
            return val ?? "";
          };
          return (
            <InputSelect
              errors={errors["sector_id"]}
              id="sector_id"
              label={CLIENT_FORM_LABELS.sector}
              placeholder={CLIENT_FORM_PLACEHOLDERS.sector}
              options={options}
              readOnly={readOnly}
              required
              maxHeight={160}
              {...props}
              setValue={handleSetValue}
              value={extractValue(value) || extractValue(props.value)}
            />
          );
        },
      }}
    />
  );
};

export const renderInputContractStatus = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  setValue,
  options,
  errors = {},
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "contractual_status_id",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSelect
          id="contractual_status_id"
          label={label}
          options={options}
          placeholder={placeholder}
          readOnly={readOnly}
          required
          setValue={setValue}
          errors={errors["contractual_status_id"]}
          errorMessage={errorMessage}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputCoefPos = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  setValue,
  syntecCoef,
  value,
  errors = {},
  errorMessage,
  name,
  required,
}) => (
  <Controller
    {...{
      control,
      register,
      name: name,
      rules: {
        required: required,
      },
      render: (props) => (
        <InputSearchTag
          errorMessage={errorMessage}
          errors={errors}
          label={label}
          limitChoice={FORM_INFO.syntec_coef_position_staff.limitChoice}
          onDelete={() => setValue(name, [])}
          options={syntecCoef.map((elm) => ({
            value: elm.id,
            label: elm.value,
          }))}
          placeholder={placeholder}
          popup={FORM_INFO.syntec_coef_position_staff.popup}
          readOnly={readOnly}
          required={required}
          selectedOptions={value}
          setValue={(val) => setValue(name, val)}
          type={TAG_TYPES.POS_COEF}
          id="syntec-coef"
          {...props}
        />
      ),
    }}
  />
);
export const renderInputPicture = ({
  label,
  value,
  readOnly,
  id,
  required,
  createFile,
  uploadProfilePicture,
  setCreatedFile,
  setCode,
  setStatus,
  isValidated,
  createdFile,
}) => (
  <InputPicture
    label={label}
    readOnly={readOnly}
    memberPictureToReplace={!!value}
    required={required}
    value={value}
    id={id}
    createFile={createFile}
    uploadProfilePicture={uploadProfilePicture}
    setCreatedFile={setCreatedFile}
    setCode={setCode}
    setStatus={setStatus}
    isValidated={isValidated}
    createdFile={createdFile}
  />
);

export const renderInputChildren = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "children_information",
      rules: {
        pattern: RULES.positive_max_99,
      },
      render: (props) => (
        <InputText
          errors={errors["children_information"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          type={VAR_TYPES.NUMBER}
          id="children_information"
          {...props}
        />
      ),
    }}
  />
);
export const renderInputAddress = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
  id,
  setValue,
  value,
  height,
  width,
  errorMessage,
  errorValue,
  rows,
  dynamicHeight,
  defaultDynamicHeight,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "address",
      rules: {
        required: true,
        pattern: {
          value: RULES.min_5_max_500_char,
          message: "api_error_address",
        },
      },
      render: (props) => (
        <InputTextArea
          errorValue={errorValue}
          errorMessage={errorMessage}
          readOnly={readOnly}
          label={label}
          id={id}
          width={width}
          height={height}
          setValue={setValue}
          placeholder={placeholder}
          required={true}
          errors={errors["address"]}
          rows={rows}
          dynamicHeight={dynamicHeight}
          defaultDynamicHeight={defaultDynamicHeight}
          {...props}
        />
      ),
    }}
  />
);
export const renderInputBirthtown = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "birth_place",
      rules: {
        required: true,
        pattern: {
          value: RULES.min_2_max_64_no_spaces,
        },
      },
      render: (props) => (
        <InputText
          errors={errors["birth_place"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          id="birth_place"
          errorMessage={errorMessage}
          {...props}
        />
      ),
    }}
  />
);
export const renderInputDegree = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "degree",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputText
          errorMessage={errorMessage}
          errors={errors["degree"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          id="degree"
          {...props}
        />
      ),
    }}
  />
);
export const renderInputYearsExperience = ({
  comment,
  commentName,
  control,
  label,
  placeholder,
  readOnly,
  register,
  setValue,
  errors = {},
  name,
  errorMessage,
}) => (
  <Controller
    {...{
      comment,
      control,
      register,
      name: name || "recruitment_experience",
      rules: {
        pattern: {
          value: RULES.positive_max_99,
        },
        required: true,
      },
      render: (props) => (
        <InputText
          errors={errors[name || "recruitment_experience"]}
          label={label}
          placeholder={placeholder}
          readOnly={readOnly}
          required
          comment={comment}
          commentSettings={FORM_INFO.years_experience.comment_settings}
          setComment={(val) => setValue(commentName, val)}
          type={VAR_TYPES.NUMBER}
          id={name || "recruitment_experience"}
          errorMessage={errorMessage}
          {...props}
        />
      ),
    }}
  />
);
export const renderInputRecruitmentMode = ({
  control,
  label,
  placeholder,
  readOnly,
  recruitmentMethods,
  register,
  setValue,
  value,
  required = false,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "recruitment_mode_id",
      rules: {
        required: required,
      },
      render: (props) => (
        <InputSearchTag
          required={required}
          errors={errors["recruitment_mode_id"]}
          label={label}
          limitChoice={FORM_INFO.recruitment_mode.limitChoice}
          onDelete={() => setValue("recruitment_mode_id", [])}
          options={recruitmentMethods}
          placeholder={placeholder}
          popup={FORM_INFO.recruitment_mode.popup}
          readOnly={readOnly}
          selectedOptions={value}
          setValue={(val) => setValue("recruitment_mode_id", val)}
          type={TAG_TYPES.RECRUITMENT_MODE}
          id="recruitment-mode"
          {...props}
        />
      ),
    }}
  />
);
export const renderInputCooptedBy = ({
  control,
  getOptions,
  label,
  placeholder,
  readOnly,
  recruitment_mode_id,
  register,
  setValue,
  value,
  personsList,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "referer_internal_id",
      rules: {
        required:
          recruitment_mode_id[0]?.value === RECRUITMENT_METHOD.REFERER_INTERNAL,
      },
      render: (props) => (
        <InputSearchTag
          errors={errors["referer_internal_id"]}
          getOptions={(search) => getOptions(search)}
          label={label}
          limitChoice={FORM_INFO.coopted_by.limitChoice}
          minLength={2}
          onDelete={() => setValue("referer_internal_id", [])}
          options={formatAllUsers(personsList, TAG_TYPES.PEOPLE)}
          placeholder={placeholder}
          popup={FORM_INFO.coopted_by.popup}
          readOnly={readOnly}
          required
          selectedOptions={value}
          setValue={(val) => setValue("referer_internal_id", val)}
          type={TAG_TYPES.PEOPLE}
          id="referer-internal"
          {...props}
        />
      ),
    }}
  />
);

export const renderRefererExternal = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "referer_external",
      rules: {
        required: false,
      },
      render: (props) => (
        <InputText
          errors={errors["referer_external"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          {...props}
          id="referer_external"
        />
      ),
    }}
  />
);

export const renderPhonenumber = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  required,
  errors = {},
  name,
  value,
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: name || "phone_number",
      rules: {
        required: required,
        pattern: {
          value: RULES.phone_number,
          message: "invalid_charactere",
        },
      },
      render: (props) => (
        <InputText
          errors={errors[`${name || "phone_number"}`]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required={required}
          value={value}
          {...props}
          id={`${name || "phone_number"}`}
          errorMessage={errorMessage}
        />
      ),
    }}
  />
);
export const renderInputYear = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      commentName: "comment",
      control,
      register,
      name: "degree_year",
      rules: {
        required: true,
        pattern: {
          value: RULES.year,
        },
      },
      render: (props) => (
        <InputText
          id="degree_year"
          errors={errors["degree_year"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          maxLength={4}
          pattern={RULES.positive_max_9999}
          {...props}
        />
      ),
    }}
  />
);
export const renderInputNationality = ({
  control,
  label,
  nationalities = [],
  placeholder,
  readOnly,
  register,
  setValue,
  value,
  lang,
  errors = {},
  errorMessage,
  name,
}) => {
  return (
    <Controller
      {...{
        control,
        register,
        name: name,
        rules: {
          required: true,
        },
        render: (props) => (
          <InputSearchList
            errorMessage={errorMessage}
            label={label}
            limitChoice={FORM_INFO.nationality.limitChoice}
            onDelete={() => setValue(name, [])}
            options={nationalities?.map((elm) => ({
              label:
                lang === LANG.FR ? elm?.nationality_fr : elm?.nationality_eng,
              value: elm?.id,
            }))}
            placeholder={placeholder}
            popup={FORM_INFO.nationality.popup}
            readOnly={readOnly}
            required
            selectedOptions={
              (lang === LANG.FR
                ? value?.nationality_fr
                : value?.nationality_eng) || value[0]?.label
            }
            setValue={(val) => setValue(name, val)}
            errors={errors[name]}
            {...props}
          />
        ),
      }}
    />
  );
};

export const renderInputNationalityIds = ({
  control,
  label,
  nationalities = [],
  placeholder,
  readOnly,
  register,
  setValue,
  lang,
  value,
  name,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: name || "nationality",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSearchList
          label={label}
          limitChoice={FORM_INFO.nationality.limitChoice}
          onDelete={() => setValue(name || "nationality", [])}
          options={nationalities.map((elm) => ({
            value: elm?.id,
            label:
              lang === LANG.FR ? elm?.nationality_fr : elm?.nationality_eng,
          }))}
          placeholder={placeholder}
          popup={FORM_INFO.nationality.popup}
          readOnly={readOnly}
          required
          selectedOptions={value[0]?.value ? value : []}
          setValue={(val) => setValue(name || "nationality", val)}
          errors={errors[name || "nationality"]}
          id={name || "nationality"}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputGender = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  value,
  setValue,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      name: "gender",
      register,
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSelect
          id="gender"
          readOnly={readOnly}
          value={value}
          label={label}
          options={GENDER_OPTIONS}
          placeholder={placeholder}
          required
          setValue={setValue}
          errors={errors["gender"]}
          {...props}
        />
      ),
    }}
  />
);
export const renderFirstName = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  name,
  errors = {},
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: name || "first_name",
      rules: {
        required: true,
        pattern: {
          value: RULES.min_2_max_64_no_spaces,
        },
      },
      render: (props) => (
        <InputText
          errors={errors[name || "first_name"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          {...props}
          id="first_name"
          errorMessage={errorMessage}
        />
      ),
    }}
  />
);
export const renderLastName = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  name,
  errors = {},
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      name: name || "last_name",
      register,
      rules: {
        required: true,
        pattern: {
          value: RULES.min_1_max_64_no_spaces,
        },
      },
      render: (props) => (
        <InputText
          errors={errors[name || "last_name"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          id="last_name"
          errorMessage={errorMessage}
          {...props}
        />
      ),
    }}
  />
);
export const renderBirthday = ({
  control,
  label,
  id,
  lang,
  readOnly,
  register,
  errors = {},
  setValue,
  value,
}) => {
  register("birthday", { required: true });
  return (
    <InputDate
      id={id}
      readOnly={readOnly}
      label={label}
      required
      errors={errors["birthday"]}
      lang={lang}
      onChange={(e) => setValue("birthday", e.target.value)}
      value={value}
    />
  );
};

export const renderInputEmail = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
  name,
  errorMessage,
  setCode,
  setStatus,
  copyEmail,
}) => (
  <Controller
    {...{
      control,
      register,
      name: name || "email",
      rules: {
        required: true,
        pattern: {
          value: RULES.email,
          message: "invalid_email",
        },
      },
      render: (props) => (
        <InputText
          errors={errors[name || "email"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          type={VAR_TYPES.STRING}
          id={name || "email"}
          errorMessage={errorMessage}
          setCode={setCode}
          setStatus={setStatus}
          copyEmail={copyEmail}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputFunction = ({
  control,
  label,
  placeholder,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "function",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputText
          id="function"
          errors={errors["function"]}
          label={label}
          placeholder={placeholder}
          required
          type={VAR_TYPES.STRING}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputInterviewDate = ({
  control,
  label,
  register,
  showDatepicker,
  lang,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "interview_date",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputDate
          id={2}
          showDatepicker={showDatepicker}
          errors={errors["interview_date"]}
          label={label}
          pattern={RULES.date}
          type={VAR_TYPES.STRING}
          lang={lang}
          required
          {...props}
        />
      ),
    }}
  />
);

export const renderInputCdiStartDate = ({
  control,
  label,
  register,
  showDatepicker,
  errors = {},
  lang,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "cdi_start_date",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputDate
          id={3}
          errors={errors["cdi_start_date"]}
          label={label}
          showDatepicker={showDatepicker}
          pattern={RULES.date}
          lang={lang}
          required
          type={VAR_TYPES.STRING}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputLastInterviewDate = ({
  control,
  label,
  register,
  showDatepicker,
  lang,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "last_interview_date",
      render: (props) => (
        <InputDate
          id={4}
          showDatepicker={showDatepicker}
          errors={errors["last_interview_date"]}
          label={label}
          pattern={RULES.date}
          lang={lang}
          type={VAR_TYPES.STRING}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputLastInterviewSalaryEvolution = ({
  control,
  label,
  placeholder,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "salary_evolution",
      render: (props) => (
        <InputText
          id="salary_evolution"
          errors={errors["salary_evolution"]}
          label={label}
          placeholder={placeholder}
          type={VAR_TYPES.STRING}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputLastInterviewProfessionalEvolution = ({
  control,
  label,
  placeholder,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "professional_evolution",
      render: (props) => (
        <InputText
          id="professional_evolution"
          errors={errors["professional_evolution"]}
          label={label}
          placeholder={placeholder}
          type={VAR_TYPES.STRING}
          {...props}
        />
      ),
    }}
  />
);
export const renderInputYearsExperienceSC = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "years_of_experience",
      rules: {
        pattern: {
          value: RULES.positive_max_99,
        },
      },
      render: (props) => (
        <InputText
          id="years_of_experience"
          errors={errors["years_of_experience"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          type={VAR_TYPES.NUMBER}
          {...props}
        />
      ),
    }}
  />
);
export const renderInputCegid = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      name: "cegid_id",
      register,
      render: (props) => (
        <InputText
          id="cegid_id"
          errors={errors["cegid_id"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputDiploma = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "diploma",
      render: (props) => (
        <InputText
          id="diploma"
          errors={errors["diploma"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          {...props}
        />
      ),
    }}
  />
);

export const renderDisplayReadOnlyField = ({
  control,
  label,
  register,
  errors = {},
  name,
  isUnderline,
  onClick,
}) => (
  <Controller
    {...{
      control,
      register,
      name,
      rules: {
        required: true,
      },
      render: (props) => (
        <InputText
          id="read_only_field"
          errors={errors[name]}
          readOnly={true}
          label={label}
          required
          isUnderline={isUnderline}
          onClick={onClick}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputTvaRegime = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
  setValue,
  options,
}) => (
  <Controller
    {...{
      control,
      register,
      rules: {
        required: true,
      },
      name: "tva_regime_id",
      render: (props) => (
        <InputSelect
          id="tva_regime_id"
          maxHeight={160}
          errors={errors["tva_regime_id"]}
          label={label}
          options={options}
          placeholder={placeholder}
          readOnly={readOnly}
          required
          setValue={setValue}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputPaymentCondition = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
  setValue,
  options,
}) => (
  <Controller
    {...{
      control,
      register,
      rules: {
        required: true,
      },
      name: "payment_condition_id",
      render: (props) => (
        <InputSelect
          id="payment_condition_id"
          maxHeight={160}
          errors={errors["payment_condition_id"]}
          readOnly={readOnly}
          label={label}
          options={options}
          placeholder={placeholder}
          setValue={setValue}
          {...props}
          required
        />
      ),
    }}
  />
);

export const renderCompanyName = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
  name,
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: name || "company_name",
      rules: {
        required: true,
        validate: (value) => value.length <= MAX_LENGTH,
      },
      render: (props) => (
        <InputText
          errors={errors[name || "company_name"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          id={name || "company_name"}
          errorMessage={errorMessage}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputBillingType = ({
  control,
  errors = {},
  label,
  placeholder,
  readOnly,
  register,
  setValue,
  value,
  errorValue,
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "billing_type",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSelect
          errorMessage={errorMessage}
          errorValue={errorValue}
          id="billing_type"
          errors={errors["billing_type"]}
          label={label}
          value={value}
          placeholder={placeholder}
          readOnly={readOnly}
          required
          options={BILLING_TYPE}
          setValue={setValue}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputBillingCountries = ({
  control,
  errors,
  options,
  label,
  placeholder,
  readOnly,
  register,
  setValue,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "billing_country",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSelect
          id="billing_country"
          maxHeight={160}
          label={label}
          placeholder={placeholder}
          options={options}
          required
          setValue={setValue}
          readOnly={readOnly}
          errors={errors["billing_country"]}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputBillingPostalCode = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "billing_postal_code",
      rules: {
        required: true,
        pattern: {
          value: RULES.pattern_zip_code,
        },
      },
      render: (props) => (
        <InputText
          errors={errors["billing_postal_code"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          maxLength={9}
          id="billing_postal_code"
          {...props}
        />
      ),
    }}
  />
);

export const renderInputIntercommunityTvaNumber = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "intercommunity_tva_number",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputText
          id="intercommunity_tva_number"
          errors={errors["intercommunity_tva_number"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          {...props}
        />
      ),
    }}
  />
);

export const renderAuxiliaryCode = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "auxiliary_code",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputText
          id="auxiliary_code"
          errors={errors["auxiliary_code"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          {...props}
        />
      ),
    }}
  />
);
export const renderSiret = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "siret",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputText
          id="siret"
          errors={errors["siret"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          errorMessage={errorMessage}
          {...props}
        />
      ),
    }}
  />
);

export const renderSiren = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
  errorMessage,
  onClickSearch,
  value,
  nationalityId,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "siren",
      rules: {
        required: true,
        validate: (val) => {
          if (
            nationalityId === FRANCE_NATIONALITY_ID &&
            !isValidSirenLuhn(val)
          )
            return "french_siren_should_validate_luhn_algorithm";
          return true;
        },
      },
      render: (props) => (
        <InputTextSearch
          errors={errors["siren"]}
          id="siren"
          label={label}
          placeholder={placeholder}
          onClickSearch={onClickSearch}
          readOnly={readOnly}
          required
          value={value}
          errorMessage={errorMessage}
          maxlength={"9"}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputCountry = ({
  countries = [],
  control,
  label,
  placeholder,
  readOnly,
  register,
  setValue,
  value = [],
  lang,
  errors = {},
  name,
}) => (
  <Controller
    {...{
      control,
      register,
      name: name || "country",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSearchList
          label={label}
          onDelete={() => setValue(name || "country", [])}
          options={countries?.map((elm) => ({
            label: lang === LANG.FR ? elm?.name_fr : elm?.name,
            value: elm?.id,
          }))}
          placeholder={placeholder}
          popup={FORM_INFO.country.popup}
          readOnly={readOnly}
          required
          selectedOptions={formattedCountry({ countries, value, lang })}
          setValue={(val) => setValue(name || "country", val)}
          errors={errors[name || "country"]}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputCity = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "city",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputText
          id="city"
          errors={errors["city"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          {...props}
        />
      ),
    }}
  />
);

export const renderInputBillingCity = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "billing_city",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputText
          errors={errors["billing_city"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          id="billing_city"
          {...props}
        />
      ),
    }}
  />
);

export const renderInputPostalCode = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "postal_code",
      rules: {
        required: true,
        pattern: {
          value: RULES.pattern_zip_code,
        },
      },
      render: (props) => (
        <InputText
          id="postal_code"
          errors={errors["postal_code"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          maxLength={9}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputCoordinator = ({
  control,
  getNamesByFilters,
  label,
  readOnly,
  register,
  namesByFilters,
  setValue,
  value,
  errors = {},
  profile = ["cslt", "str"],
}) => (
  <Controller
    {...{
      control,
      register,
      name: "rate_coordinator",
      render: (props) => (
        <InputSearchTag
          errors={errors["rate_coordinator"]}
          getOptions={(search) =>
            getNamesByFilters({
              types: [FILTERS_FOR_NAMES.PERSON],
              profile,
              search,
            })
          }
          label={label}
          limitChoice={1}
          minLength={2}
          onDelete={() => setValue("rate_coordinator", [])}
          options={formatUsersWithProfile(
            namesByFilters?.filter((elm) => !elm?.deleted),
            profile,
            TAG_TYPES.PEOPLE
          )}
          popup={FORM_INFO.eosUsers.popup}
          readOnly={readOnly}
          selectedOptions={value}
          setValue={(val) => setValue("rate_coordinator", val)}
          id="rate_coordinator"
          {...props}
        />
      ),
    }}
  />
);

export const renderInputAccountingFirstName = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "accounting_contact_first_name",
      render: (props) => (
        <InputText
          errors={errors["accounting_contact_first_name"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          id="accounting_contact_first_name"
          {...props}
        />
      ),
    }}
  />
);

export const renderInputAccountingLastName = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "accounting_contact_last_name",
      render: (props) => (
        <InputText
          errors={errors["accounting_contact_last_name"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          id="accounting_contact_last_name"
          {...props}
        />
      ),
    }}
  />
);

export const renderInputBillingAddress = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "billing_address",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputText
          errors={errors["billing_address"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          id="billing_address"
          {...props}
        />
      ),
    }}
  />
);
export const renderInputBranches = ({
  setValue,
  label,
  branchList,
  readOnly,
  watch,
  errors,
}) => (
  <InputSearchTag
    label={label}
    limitChoice={1}
    onDelete={() => setValue("affiliate_id", [])}
    options={formatNames(branchList)}
    popup={FORM_INFO.branch.popup}
    readOnly={readOnly}
    required
    selectedOptions={watch("affiliate_id")}
    setValue={(val) => setValue("affiliate_id", val)}
    type={TAG_TYPES.BRANCHES}
    errors={errors["affiliate_id"]}
    id="affiliate"
  />
);

export const renderInputAffiliate = ({
  control,
  getOptions,
  label,
  clientsList,
  readOnly,
  register,
  setValue,
  value,
  errors,
  onDelete,
  errorValue,
  name,
  errorMessage,
  required,
  isAuthorizedManager = true,
}) => (
  <Controller
    {...{
      control,
      register,
      name: name || "affiliate_id",
      rules: {
        required: required,
        validate: () =>
          isAuthorizedManager || "manager_not_authorized_for_this_client",
      },
      render: (props) => (
        <InputSearchTag
          errorMessage={errorMessage}
          errorValue={errorValue}
          errors={errors}
          getOptions={getOptions}
          label={label}
          limitChoice={1}
          required={required}
          minLength={2}
          onDelete={onDelete || (() => setValue(name || "affiliate_id", []))}
          options={clientsList}
          popup={FORM_INFO.clientFilter.popup}
          readOnly={readOnly}
          selectedOptions={value}
          setValue={(val) => setValue(name || "affiliate_id", val)}
          type={TAG_TYPES.CLIENTS}
          id={"affiliate_id"}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputAuthorizedManagers = ({
  control,
  label,
  readOnly,
  register,
  setValue,
  value,
  errors,
  errorMessage,
  getNamesByFilters,
  namesByFilters,
}) => (
  <Controller
    {...{
      control,
      register,
      name: "assigned_managers",
      render: (props) => (
        <InputSearchTag
          errors={errors}
          errorMessage={errorMessage}
          getOptions={(search) =>
            getNamesByFilters({
              role: MISSION_MANAGER_ROLES,
              types: [FILTERS_FOR_NAMES.PERSON],
              search,
            })
          }
          label={label}
          limitChoice={FORM_INFO.assignedManagers.limitChoice}
          minLength={2}
          onDelete={(val) =>
            setValue(
              "assigned_managers",
              value?.filter((elem) => elem.value !== val)
            )
          }
          options={formatUsersOptions({
            users: namesByFilters?.filter((elm) => {
              const isValid = (v) => v != null && v !== "" && v !== "null";
              return isValid(elm?.first_name) && isValid(elm?.last_name);
            }),
            type: TAG_TYPES.PEOPLE,
          })}
          popup={FORM_INFO.assignedManagers.popup}
          readOnly={readOnly}
          selectedOptions={value}
          setValue={(val) => setValue("assigned_managers", val)}
          type={TAG_TYPES.PEOPLE}
          id="assigned_managers"
          {...props}
        />
      ),
    }}
  />
);

export const renderSchool = ({
  control,
  label,
  placeholder,
  readOnly,
  register,
  name,
  errors = {},
  errorMessage,
}) => (
  <Controller
    {...{
      control,
      register,
      name: name || "school",
      rules: {
        required: true,
        pattern: RULES.min_2_max_200_char,
      },
      render: (props) => (
        <InputText
          errorMessage={errorMessage}
          errors={errors[name || "school"]}
          readOnly={readOnly}
          label={label}
          placeholder={placeholder}
          required
          {...props}
          id="school"
        />
      ),
    }}
  />
);

export const renderSchoolLevel = ({
  control,
  label,
  readOnly,
  register,
  setValue,
  options,
  errors = {},
}) => (
  <Controller
    {...{
      control,
      register,
      name: "school_level",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSelect
          id="school_level"
          label={label}
          options={options}
          required
          setValue={setValue}
          readOnly={readOnly}
          errors={errors["school_level"]}
          maxHeight={160}
          {...props}
        />
      ),
    }}
  />
);

export const renderInputSkills = ({ control, register, skills }) => (
  <Controller
    {...{
      control,
      register,
      name: "skills",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSearchTag
          label={{
            id: "skills_label",
            defaultMessage: "Skills",
          }}
          limitChoice={10}
          options={skills}
          readOnly
          selectedOptions={skills || []}
          type={TAG_TYPES.SKILLS}
          errors={{}}
          id="skills"
          {...props}
        />
      ),
    }}
  />
);

export const renderInputMobility = ({ control, register, mobilities }) => (
  <Controller
    {...{
      control,
      register,
      name: "mobility",
      rules: {
        required: true,
      },
      render: (props) => (
        <InputSearchTag
          label={{
            id: "mobility_label",
            defaultMessage: "Mobilité",
          }}
          limitChoice={10}
          options={mobilities}
          readOnly
          selectedOptions={mobilities || []}
          type={TAG_TYPES.MOBILITY}
          errors={{}}
          id="skills"
          {...props}
        />
      ),
    }}
  />
);
